import { validateContactInputs } from "./utils/contact-validation.js";
import { sumTotalPartyMembers } from "./data-to-ui-adapters/data-transform-functions.js";
import { AvailabilityType } from "./enums/availability-type.js";
import { getBookingState, getBookingStateProp, getUnitData } from "./state.js";
import { elById, qs, qsAll } from "./utils/dom.js";
import { 
	allDropdownsInSectionValid,
	isDropdownInputValid,
	isStartEndDateSelected,
	isWithinOccupancyRange,
	checkPhoneInputs,
	setControlValidationState
} from "./utils/validation-utils.js";


function isStage1Valid() {
    // const bookingData = getBookingState();
    let { 
        unit: unitId, 
        party_members: partyMembers, 
        party_size: partySize,
        availability_type: availabilityType
    } = getBookingState();

    const unit              = getUnitData(unitId);
    const numOfPartyMembers = Object.keys(partyMembers).length === 0 
                                ? partySize 
                                : sumTotalPartyMembers(partyMembers);
                                
    // Run all of the validation logic
    const locationDropdownsValid = allDropdownsInSectionValid('#atb-category-unit-section');
    const datesValid             = isStartEndDateSelected();
    let hasValidPartyMembers     = isWithinOccupancyRange(numOfPartyMembers, unit);

    // When the sub-unit row is visible but no unit is available (e.g. "NO UNITS AVAILABLE" message is
    // showing), sub_unit in state is null. The existing dropdown validator misses this because
    // setDropdownMessage puts a non-empty string in the input value. Check state directly instead.
    const subUnitRow     = elById('atb-sub-unit-row');
    const subUnitVisible = subUnitRow && subUnitRow.offsetParent !== null;
    const subUnitsValid  = !subUnitVisible || !!getBookingStateProp('sub_unit');

    if (!subUnitsValid) {
        const subUnitDropdown = elById('atb-select-dropdown-sub-unit');
        subUnitDropdown?.classList.add('is-invalid');
        elById('atb-sub-unit')?.classList.add('is-invalid');
    }


    // If the unit uses the occupancy availability type, the party size dropdown will be above the calendar
    // This party-size dropdown sets party-size in state, but changing the period of stay by default resets party-size
    // TODO: Really do not like this - party size should be evaluated using state
    if (hasValidPartyMembers && availabilityType == AvailabilityType.Occupancy) {
        const occPartySizeDropdownInput = elById('atb-party-size2');
        partySize = parseIntb10(occPartySizeDropdownInput.dataset.selectedId);
        hasValidPartyMembers = partySize == numOfPartyMembers;
    }

	const calendarButton = elById('atb-calendar-btn');
	const dateValidationMessage = elById('atb-period-invalid');
	if (!datesValid)
		dateValidationMessage.classList.add('d-block');
	else
		dateValidationMessage.classList.remove('d-block');
	setControlValidationState(calendarButton, dateValidationMessage, datesValid);

    const isPartySizeMismatched = availabilityType == AvailabilityType.Occupancy && partySize !== numOfPartyMembers;

    // Show/hide the validation message depending upon party member validity
    showInvalidPartyMembersMessages(            
        isPartySizeMismatched, // show the occupancy party size message
        !hasValidPartyMembers && Object.keys(partyMembers).length !== 0
    );


    return locationDropdownsValid &&
        datesValid &&
        hasValidPartyMembers &&
        subUnitsValid;
}

function showInvalidPartyMembersMessages(showMismatchMessage, showMaxOccMessage) {                
    const [ partySizeMismatchMessage, exceedMaxOccupancyMessage ] = qsAll('#atb-party-members-section .invalid-feedback');
    const partyMembersSection = qs('#atb-party-members-section');

    partySizeMismatchMessage.classList.toggle('d-block', showMismatchMessage);
    exceedMaxOccupancyMessage.classList.toggle('d-block', showMaxOccMessage);
    setControlValidationState(partyMembersSection, partySizeMismatchMessage, !showMismatchMessage);
    if (showMaxOccMessage) {
        setControlValidationState(partyMembersSection, exceedMaxOccupancyMessage, false);
    }
}

function isStage2Valid() {    
    const extrasSectionEl = document.querySelector('#atb-extras-section');
    //const extrasDropdownsValid = allDropdownsInSectionValid('#atb-extras-section');    
    
    // Get all visible extras
    const visibleExtras = Array.from(extrasSectionEl.querySelectorAll('.atb-addon:not(.d-none)'));
    
    let allExtrasValid = true;
    let invalidExtraEl = null;
    visibleExtras.forEach((extra) => {
        const input             = extra.querySelector('input[required], textarea[required], .atb-dropdown[aria-required="true"]');
        const validationMessage = extra.querySelector('.atb-validation-message');

        // Skip if there's no required input
        if (!input) return; 

        let isValid = true;
        let focusTarget = input;

        // Validate based on input type
        if (input.matches('input[type="checkbox"]')) {
            isValid = input.checked;
        } 
        else if (input.matches('input[type="text"], input[type="email"], textarea')) {
            isValid = input.value.trim() !== "";
        } 
        else if (input.classList.contains('atb-dropdown')) {
            isValid = isDropdownInputValid(input);
            focusTarget = input.querySelector('input');
        }

	    // Update UI
	    validationMessage.classList.toggle('d-block', !isValid);
	    setControlValidationState(focusTarget, validationMessage, isValid);
	    allExtrasValid = allExtrasValid && isValid;

        // Track first invalid element for scroll/focus
        if (!isValid && !invalidExtraEl) {
            invalidExtraEl = focusTarget;
        }
    });


    // Focus on the first found invalid extra
    if (invalidExtraEl)
        invalidExtraEl.focus();

    return allExtrasValid;
}


function isStage3Valid() {    
    // const onLocal = window.location.hostname.includes('.local');
    const stage3Content = document.querySelector('#atb-pane-main-party-details');
  
    // If we're not logged in when we click on the continue button, try logging in and return early to avoid running the rest of the function 
    if (!isLoggedIn()) {
        tryLogin();
        return;
    }
    
    const leadBookerForm   = stage3Content.querySelector('#atb-party-details-lead-booker');
    const partyDetailsForm = stage3Content.querySelector('#atb-party-details-form');

    // TODO: should only execute this code if the subform is visible/being used
    const useSameAddressCheckboxes = partyDetailsForm.querySelectorAll('input[type="checkbox"].use-same-address');
    useSameAddressCheckboxes.forEach((checkbox) => {
        // Pull the party member id out of the element's id
        const pmId = checkbox.id.replace(/^atb-|-use-same-address$/g, '');
        const partyMemberType = pmId;

        // Skip the template party member
        if (partyMemberType == "type-1")
            return;

        if (checkbox.checked)
            populateAddressFromLeadBooker(pmId);
    });

    validateContactInputs(leadBookerForm, partyDetailsForm);
    const isLeadBookerFormValid = leadBookerForm.checkValidity();
    const isPartyDetailsFormValid = partyDetailsForm.checkValidity();
    const isDropdownInputValid    = allDropdownsInSectionValid('#atb-main-party-section');
    
    // Enable the validation messages
	if(!isLeadBookerFormValid) {
	    leadBookerForm.classList.add('was-validated');
	} else {
	    leadBookerForm.classList.remove('was-validated');
	}
	syncNativeFormValidationState(leadBookerForm);

	// Enable the validation messages
	if (!isPartyDetailsFormValid) {
	    partyDetailsForm.classList.add('was-validated');
	} else {
	    partyDetailsForm.classList.remove('was-validated');
	}
	syncNativeFormValidationState(partyDetailsForm);
    
    const allFormElementsValid = 
        isLeadBookerFormValid && 
        isDropdownInputValid && 
        isPartyDetailsFormValid;

    // Submit the forms only if the party details and lead booker form - including the dropdowns too - are valid
    if (!allFormElementsValid) {
        // Find and focus on the first invalid form element
        // doing it in this else block because we're relying on validation to return true if VISIBLE inputs are valid, hence we do not want to focus on invisible input
        let firstInvalidFormEl = leadBookerForm.querySelector('input.form-control:invalid, input.is-invalid');

        firstInvalidFormEl = firstInvalidFormEl 
            ? firstInvalidFormEl 
            : partyDetailsForm.querySelector('input.form-control:invalid, input.is-invalid');

        firstInvalidFormEl?.focus();
    }

    return allFormElementsValid;
}


function isStage4Valid() {
    // Use bootstrap's built in validation to see if the inputs aren't empty    
	const isTermsChecked = document.getElementById('atb-terms').checked;
	const validationMessage = document.querySelector('#atb-terms-row .atb-validation-message');
	const termsCheckbox = document.getElementById('atb-terms');

	toggle(validationMessage, !isTermsChecked);
	setControlValidationState(termsCheckbox, validationMessage, isTermsChecked);

    const allStagesValid = isStage1Valid() && isStage2Valid() && isStage3Valid();

    // if all of the previous stages have not been properly completed, it's most likely a crawler submitting the form
    // so return false - show a warning modal incase
    // TODO: possibly include a "contact us" message to help us further diagnose the issue if's a human user
    if (!allStagesValid) {
        showWarningModal('Please ensure all previous stages are completed before proceeding.');
        return false;
    }

    return allStagesValid && isTermsChecked;
}

function isXCoverStageValid() {
    const selectedOption    = document.querySelector('input[name="booking-protection"]:checked');
    const validationMessage = document.querySelector('#atb-xcover-panel .atb-validation-message');

    const canProtectBooking = getBookingStateProp('can_protect_booking');

    // If the booking doesn't qualify for refund protection, skip validation
    if (!canProtectBooking) {
        return true;
    }

    // if at least one option is selected, move to the next stage
	if (!selectedOption) {
	    toggle(validationMessage, true);
	    const xcoverPanel = document.querySelector('#atb-xcover-panel');
	    setControlValidationState(xcoverPanel, validationMessage, false);
	    return false;
	} else {
	    toggle(validationMessage, false);
	    const xcoverPanel = document.querySelector('#atb-xcover-panel');
	    setControlValidationState(xcoverPanel, validationMessage, true);
	}

    // No validation required for xcover stage as it's optional
	return true;
}

function syncNativeFormValidationState(form) {
    if (!form) return;

    form.querySelectorAll('input, select, textarea').forEach((field) => {
        if (field.type === 'hidden' || field.hasAttribute('data-is-js-template')) return;

        const validationMessage = field
            .closest('.input-wrapper-row, .form-check, .col-12')
            ?.querySelector('.atb-validation-message.invalid-feedback');
        setControlValidationState(field, validationMessage, field.checkValidity());
    });
}


export {
    isStage1Valid,
    isStage2Valid,
    isStage3Valid,
    isStage4Valid,
    isXCoverStageValid
}
